import React, { useState, useEffect } from 'react';
import SideMenu from './SideMenu';
import { goalService, roadmapService, userService, assessmentService } from '../services/api'; 
import './GoalsPage.css';
import { Trash2, CheckCircle, Target, Play, Plus, ChevronRight, ChevronLeft } from 'react-feather'; // Enhanced icons
import { useTheme } from '../ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';

const GoalsPage = () => {
  const [goals, setGoals] = useState([]);
  const [activeGoal, setActiveGoal] = useState(null);
  const [newGoalDescription, setNewGoalDescription] = useState('');
  const [goalUpdateStatus, setGoalUpdateStatus] = useState({ status: '', message: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState(null);
  const { theme } = useTheme();
  const [regenerating, setRegenerating] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState({ text: '', subtext: '' });
  const [isNewUser, setIsNewUser] = useState(false);

  // Add state for rate limit errors
  const [rateLimitError, setRateLimitError] = useState(null);
  const [rateLimitType, setRateLimitType] = useState(null); // 'goal' or 'roadmap'

  // Planning flow state
  const [planningLoading, setPlanningLoading] = useState(false);
  const [planningResult, setPlanningResult] = useState(null); // { clarifyingQuestion, goal: { courses: [...] } }
  const [clarifications, setClarifications] = useState([]); // [{question, answer}]
  const [currentClarifyingAnswer, setCurrentClarifyingAnswer] = useState('');

  // New state for assessment
  const [showAssessmentPrompt, setShowAssessmentPrompt] = useState(false);
  const [chosenGoalDescription, setChosenGoalDescription] = useState('');
  const [selectedPlannedCourseIndices, setSelectedPlannedCourseIndices] = useState({});
  
  // State for close confirmation
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  
  // Assessment taking states
  const [showAssessmentQuestions, setShowAssessmentQuestions] = useState(false);
  const [currentAssessment, setCurrentAssessment] = useState(null);
  const [assessmentAnswers, setAssessmentAnswers] = useState({});
  const [submittingAssessment, setSubmittingAssessment] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentTopicIndex, setCurrentTopicIndex] = useState(0);
  const [parsedQuestions, setParsedQuestions] = useState(null); // Now contains topics structure

  // Topic selection states
  const [showTopicSelection, setShowTopicSelection] = useState(false);
  const [topicRecommendations, setTopicRecommendations] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState({});
  const [evaluatingAssessment, setEvaluatingAssessment] = useState(false);

  // Subscription and goal limit states
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showGoalLimitModal, setShowGoalLimitModal] = useState(false);
  const [goalLimitType, setGoalLimitType] = useState(null); // 'free' or 'pro'

  const navigate = useNavigate();
  const location = useLocation();

  // Helper to normalize planning response goal (now single goal instead of array)
  const getPlannedGoal = (res) => {
    if (!res) return null;
    const g = res.goal ?? res.Goal;
    return g || null;
  };

  // Helper to get normalized knowledge units array for a goal
  const getGoalCourses = (goal) => {
    if (!goal) return [];
    const k = goal.courses || goal.Courses;
    return Array.isArray(k) ? k : [];
  };

  // Enrich a list of goals with knowledge units if missing by fetching full goal
  const enrichGoalsWithCourses = async (goalsList) => {
    if (!Array.isArray(goalsList)) return [];
    const enriched = await Promise.all(
      goalsList.map(async (g) => {
        const hasCourses = Array.isArray(g?.courses) || Array.isArray(g?.Courses);
        if (hasCourses) return g;
        try {
          const full = await goalService.getGoal(g.id || g.Id);
          const courses = Array.isArray(full?.courses) ? full.courses : (Array.isArray(full?.Courses) ? full.Courses : []);
          return { ...g, courses };
        } catch (_) {
          return g;
        }
      })
    );
    return enriched;
  };

  // Convenience to refresh goals state including knowledge units
  const refreshGoalsWithCourses = async () => {
    const base = await goalService.getUserGoals();
    const enriched = await enrichGoalsWithCourses(base);
    setGoals(enriched);
    return enriched;
  };

  // Function to check goal limits based on subscription
  const checkGoalLimits = () => {
    // Disabled goal limits for free/pro plans to avoid gating access
    // Original implementation kept below for reference.
    // if (!subscriptionStatus) { return true; }
    // const incompleteGoals = goals.filter(goal => !goal.isCompleted);
    // const incompleteCount = incompleteGoals.length;
    // if (subscriptionStatus.tier === 'free' || !subscriptionStatus.isActive) {
    //   if (incompleteCount >= 1) {
    //     setGoalLimitType('free');
    //     setShowGoalLimitModal(true);
    //     return false;
    //   }
    // } else if (subscriptionStatus.tier === 'pro' && subscriptionStatus.isActive) {
    //   if (incompleteCount >= 10) {
    //     setGoalLimitType('pro');
    //     setShowGoalLimitModal(true);
    //     return false;
    //   }
    // }
    return true;
  };

  // Function to handle "Get Pro" button click
  const handleGetPro = () => {
    setShowGoalLimitModal(false);
    navigate('/settings', { state: { activeTab: 'subscription' } });
  };

  // Function to close goal limit modal
  const closeGoalLimitModal = () => {
    setShowGoalLimitModal(false);
    setGoalLimitType(null);
  };

  // Fetch goals on mount
  useEffect(() => {
    const fetchGoals = async () => {
      setIsLoading(true);
      try {
        // Get all user goals (enriched with knowledge units)
        const userGoals = await goalService.getUserGoals();
        const enriched = await enrichGoalsWithCourses(userGoals);
        setGoals(enriched);
        
            // Get subscription status (disabled)
            try {
              setSubscriptionStatus({ tier: 'free', isActive: true, endDate: null });
            } catch (error) {
              console.error('Error fetching subscription status:', error);
              // Default to permissive subscription if error
              setSubscriptionStatus({ tier: 'free', isActive: true, endDate: null });
            }
        
        // Check if this is a new user (no goals and coming from registration)
        const fromRegistration = sessionStorage.getItem('fromRegistration');
        if (fromRegistration === 'true' && (!userGoals || userGoals.length === 0)) {
          setIsNewUser(true);
          // Clear the flag so it only happens once
          sessionStorage.removeItem('fromRegistration');
        }
    

        // Get active goal
        const currentActive = await goalService.getActiveGoal();
        setActiveGoal(currentActive);
      } catch (error) {
        console.error("Error fetching goals:", error);
        setGoalUpdateStatus({ status: 'error', message: 'Could not load goals.' });
      } finally {
        setIsLoading(false);
      }
    };

    fetchGoals();

    // Listen for sidebar toggle events
    const handleSidebarToggle = (event) => {
      setSidebarCollapsed(event.detail.collapsed);
    };
    document.addEventListener('sidebar-toggle', handleSidebarToggle);

    return () => {
      document.removeEventListener('sidebar-toggle', handleSidebarToggle);
    };
  }, []);

  const handleNextClick = async () => {
    if (!newGoalDescription.trim()) {
      setGoalUpdateStatus({
        status: 'error',
        message: 'Please enter a goal description'
      });
      return;
    }
    try {
      setPlanningLoading(true);
      const result = await goalService.planGoals(newGoalDescription.trim(), clarifications);
      if (result.status === 402) {
        document.getElementById('newGoalModal').style.display = 'none';
        document.body.style.overflow = 'hidden';
        return;
      }
      console.log('Planning result:', result);
      setPlanningResult(result);
      const clarifying = result?.clarifyingQuestion || result?.ClarifyingQuestion || '';
      const goal = result?.goal || result?.Goal || null;
      if (clarifying && clarifying.trim().length > 0) {
        // Switch UI to clarifying mode
        setGoalUpdateStatus({ status: '', message: '' });
        setCurrentClarifyingAnswer('');
      } else if (goal && goal.courses && goal.courses.length > 0) {
        // Present the planned goal and its skills for preview
        setGoalUpdateStatus({ status: '', message: '' });
        setShowAssessmentPrompt(true);
      } else {
        // Fallback to assessment
        setChosenGoalDescription(newGoalDescription);
        setShowAssessmentPrompt(true);
        setGoalUpdateStatus({ status: '', message: '' });
      }
    } catch (err) {
      console.error('Planning fetch error:', err);
      // Fallback to assessment on error
      setChosenGoalDescription(newGoalDescription);
      setShowAssessmentPrompt(true);
      setGoalUpdateStatus({ status: '', message: '' });
    } finally {
      setPlanningLoading(false);
    }
  };

  const handleClarificationNext = async () => {
    const question = planningResult?.clarifyingQuestion || planningResult?.ClarifyingQuestion || '';
    if (!question) return;
    if (!currentClarifyingAnswer.trim()) {
      setGoalUpdateStatus({ status: 'error', message: 'Please provide an answer to proceed.' });
      return;
    }
    setGoalUpdateStatus({ status: '', message: '' });
    const updated = [...clarifications, { question, answer: currentClarifyingAnswer.trim() }];
    setClarifications(updated);
    try {
      setPlanningLoading(true);
      const result = await goalService.planGoals(newGoalDescription.trim(), updated);
      setPlanningResult(result);
      const goal = result?.goal || result?.Goal || null;
      if (goal && goal.courses && goal.courses.length > 0) {
        setShowAssessmentPrompt(true);
      }
      setCurrentClarifyingAnswer('');
    } catch (e) {
      console.error('Clarification planning error:', e);
    } finally {
      setPlanningLoading(false);
    }
  };

  const handleClarificationBack = async () => {
    // If no prior clarifications, go back to goal description step
    if (!clarifications || clarifications.length === 0) {
      setPlanningResult(null);
      setCurrentClarifyingAnswer('');
      setGoalUpdateStatus({ status: '', message: '' });
      return;
    }

    // Remove the last clarification and re-fetch the previous question/state
    const updated = clarifications.slice(0, clarifications.length - 1);
    setClarifications(updated);
    setCurrentClarifyingAnswer('');
    try {
      setPlanningLoading(true);
      const result = await goalService.planGoals(newGoalDescription.trim(), updated);
      setPlanningResult(result);
      const clarifying = result?.clarifyingQuestion || result?.ClarifyingQuestion || '';
      const goal = result?.goal || result?.Goal || null;
      if (clarifying && clarifying.trim().length > 0) {
        setShowAssessmentPrompt(false);
      } else if (goal && goal.courses && goal.courses.length > 0) {
        setShowAssessmentPrompt(true);
      }
      setGoalUpdateStatus({ status: '', message: '' });
    } catch (e) {
      console.error('Clarification back error:', e);
    } finally {
      setPlanningLoading(false);
    }
  };

  const handleSetActiveGoal = async (goalId) => {
    try {
      setGoalUpdateStatus({
        status: 'loading',
        message: 'Updating active goal...'
      });

      // Set goal as active
      await goalService.setActiveGoal(goalId);
      
      // Refresh goals (include courses)
      const updatedGoals = await goalService.getUserGoals();
      const enriched = await enrichGoalsWithCourses(updatedGoals);
      setGoals(enriched);
      
      // Get the updated active goal
      const updatedActive = await goalService.getActiveGoal();
      setActiveGoal(updatedActive);

      setGoalUpdateStatus({
        status: 'success',
        message: 'Active goal updated successfully!'
      });

      // Clear success message after 3 seconds
      setTimeout(() => {
        setGoalUpdateStatus({ status: '', message: '' });
      }, 3000);
    } catch (error) {
      console.error("Error setting active goal:", error);
      setGoalUpdateStatus({
        status: 'error',
        message: error.response?.data?.message || 'Failed to update active goal'
      });
    }
  };

  // Add new function to delete a goal
  const handleDeleteGoal = (goalId) => {
    // Immediately remove the goal from local state
    setGoals(prevGoals => prevGoals.filter(goal => goal.id !== goalId));
    
    // Check if the deleted goal was active and clear it
    if (activeGoal && activeGoal.id === goalId) {
      setActiveGoal(null);
    }

    // Fire and forget - delete the goal without waiting for completion
    goalService.deleteGoal(goalId).catch(error => {
      console.error("Error deleting goal:", error);
      // Optionally show a toast notification or log the error
    });
  };

  const openDeleteModal = (goal) => {
    setGoalToDelete(goal);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setGoalToDelete(null);
  };

  const confirmDeleteGoal = () => {
    if (goalToDelete) {
      // Immediately hide the goal from UI
      setGoals(prevGoals => prevGoals.filter(goal => goal.id !== goalToDelete.id));
      
      // Check if the deleted goal was active and clear it
      if (activeGoal && activeGoal.id === goalToDelete.id) {
        setActiveGoal(null);
      }
      
      // Close modal immediately
      closeDeleteModal();
      
      // Fire and forget - delete the goal without waiting for completion
      goalService.deleteGoal(goalToDelete.id).catch(error => {
        console.error("Error deleting goal:", error);
        // Optionally show a toast notification or log the error
      });
    }
  };

  // Calculate goal progress using real data from backend
  const getGoalProgress = (goal) => {
    if (goal.isCompleted) return 100;
    
    // Use the progress data from the backend API
    // Check multiple possible property names for compatibility
    const progress = goal.progress || 
                    goal.Progress || 
                    goal.goalProgress?.completionPercentage || 
                    goal.GoalProgress?.CompletionPercentage || 
                    0;
    
    return Math.round(progress);
  };

  // Get goal progress details for display
  const getGoalProgressDetails = (goal) => {
    const completedLessons = goal.goalProgress?.completedLessons || 
                           goal.GoalProgress?.CompletedLessons || 
                           0;
    const totalLessons = goal.goalProgress?.totalLessons || 
                        goal.GoalProgress?.TotalLessons || 
                        0;
    
    return { completedLessons, totalLessons };
  };

  // Check if a goal is active
  const isGoalActive = (goal) => {
    return activeGoal && activeGoal.id === goal.id;
  };

  const handleTogglePlannedCourse = (index, courseTitle) => {
    setSelectedPlannedCourseIndices(prev => {
      const next = { ...prev };
      if (next[index]) {
        delete next[index];
      } else {
        next[index] = true;
      }
      return next;
    });
  };

  // Submit selected planned courses to backend (with clarifications and aggregated courses)
  const handleSubmitPlannedCourses = async () => {
    try {
      const plannedGoal = getPlannedGoal(planningResult);
      if (!plannedGoal) return;

      // Get selected courses based on indices
      const selectedCourseIndices = Object.keys(selectedPlannedCourseIndices)
        .map(idx => parseInt(idx))
        .filter(idx => selectedPlannedCourseIndices[idx]);

      if (selectedCourseIndices.length === 0) {
        setGoalUpdateStatus({ status: 'error', message: 'Please select at least one course.' });
        return;
      }

      const selectedCourses = selectedCourseIndices
        .map(idx => getGoalCourses(plannedGoal)[idx])
        .filter(Boolean);

      if (selectedCourses.length === 0) {
        setGoalUpdateStatus({ status: 'error', message: 'Please select at least one course.' });
        return;
      }

            // Build description from user input or planned goal title
      const description = newGoalDescription.trim() || plannedGoal.title || plannedGoal.Title || '';
      if (!description) {
        setGoalUpdateStatus({ status: 'error', message: 'Please enter a goal description.' });
        return;
      }

      // Map selected courses to backend DTO shape
      const courses = selectedCourses.map((course) => {
        const title = course.title || course.Title || '';
        const descriptionText = course.description || course.Description || '';
        const imageUrl = course.imageUrl || course.ImageUrl || null;
        const tags = Array.isArray(course.tags) ? course.tags : [];
        const realisticDuration = Number(
          course.realisticDuration || course.RealisticDuration || course.realisticDurationHours || course.RealisticDurationHours || 0
        ) || 0;
        const desiredDuration = Number(
          course.desiredDuration || course.DesiredDuration || realisticDuration || 0
        ) || 0;
        const complexityLevel = Number(course.complexityLevel || course.ComplexityLevel || 1) || 1;

        return {
          title: title,
          complexityLevel: complexityLevel,
          imageUrl: imageUrl,
          description: descriptionText,
          desiredDuration: desiredDuration,
          realisticDuration: realisticDuration,
          tags: tags
        };
      });

      // Create the goal with plan
      setGoalUpdateStatus({ status: 'loading', message: 'Creating goal...' });
      setLoaderMessage({ text: 'Creating Goal...', subtext: 'This may take a few moments.' });
      setRegenerating(true);
      document.getElementById('newGoalModal').style.display = 'none';
      document.body.style.overflow = 'hidden';

      const created = await goalService.createGoalWithPlan({
        description,
        clarifications: clarifications,
        courses
      });

      console.log('Response from Created goal with plan:', created);

      // Refresh goals (include courses) and set active
      const updatedGoals = await goalService.getUserGoals();
      const enrichedGoals = await enrichGoalsWithCourses(updatedGoals);
      setGoals(enrichedGoals);
      setActiveGoal(created);

      // Fire-and-forget: generate image
      goalService.generateGoalImage(created.id).catch(() => {});

      // Do NOT auto-generate roadmap on submit
      setRegenerating(false);
      document.body.style.overflow = '';
      setGoalUpdateStatus({ status: 'success', message: 'Goal created successfully!' });
      setTimeout(() => setGoalUpdateStatus({ status: '', message: '' }), 2000);

      // Cleanup
      resetAssessmentStates();
    } catch (error) {
      console.error('Failed to submit planned courses:', error);
      setRegenerating(false);
      document.body.style.overflow = '';
      setGoalUpdateStatus({ status: 'error', message: error.response?.data || error.message || 'Failed to create goal' });
    }
  };

  // Function to handle assessment answer selection
  const handleAssessmentAnswer = (topicIndex, questionIndex, answer) => {
    const answerKey = `${topicIndex}-${questionIndex}`;
    setAssessmentAnswers(prev => ({
      ...prev,
      [answerKey]: answer
    }));
  };

  // Function to submit assessment answers
  const handleSubmitAssessment = async () => {
    try {
      setSubmittingAssessment(true);
      setGoalUpdateStatus({ status: 'loading', message: 'Submitting assessment...' });
      
      // Structure answers by topic and question
      const structuredAnswers = {
        topics: []
      };
      
      if (parsedQuestions?.topics) {
        parsedQuestions.topics.forEach((topic, topicIndex) => {
          const topicAnswers = {
            topicTitle: topic.title,
            answers: []
          };
          
          topic.questions.forEach((question, questionIndex) => {
            const answerKey = `${topicIndex}-${questionIndex}`;
            const answer = assessmentAnswers[answerKey];
            
            topicAnswers.answers.push({
              questionIndex,
              questionType: question.type,
              question: question.question,
              answer: answer || '',
              ...(question.type === 'multiple-choice' && {
                correctAnswer: question.correctAnswer
              })
            });
          });
          
          structuredAnswers.topics.push(topicAnswers);
        });
      }
      
      // First submit the assessment
      await assessmentService.submitKnowledgeAssessment(
        currentAssessment.Id || currentAssessment.id,
        JSON.stringify(structuredAnswers)
      );
      
      setGoalUpdateStatus({ 
        status: 'loading', 
        message: 'Evaluating your answers...' 
      });
      
      // Then evaluate the assessment to get topic recommendations
      setEvaluatingAssessment(true);
      const evaluationResult = await assessmentService.evaluateKnowledgeAssessment(
        currentAssessment.Id || currentAssessment.id,
        JSON.stringify(structuredAnswers)
      );
      
      // Set topic recommendations and show topic selection
      setTopicRecommendations(evaluationResult.topics || []);
      
      // Initialize selected topics based on recommendations
      const initialSelectedTopics = {};
      (evaluationResult.topics || []).forEach((topic, index) => {
        initialSelectedTopics[index] = topic.recommendToComplete;
      });
      setSelectedTopics(initialSelectedTopics);
      
      // Hide assessment questions and show topic selection
      setShowAssessmentQuestions(false);
      setShowTopicSelection(true);
      setGoalUpdateStatus({ status: '', message: '' });
      
    } catch (error) {
      console.error('Error submitting assessment:', error);
      setGoalUpdateStatus({
        status: 'error',
        message: error.response?.data?.message || error.message || 'Failed to submit assessment'
      });
    } finally {
      setSubmittingAssessment(false);
      setEvaluatingAssessment(false);
    }
  };

  // Function to go back from assessment questions
  const handleBackFromAssessment = () => {
    setShowAssessmentQuestions(false);
    setShowAssessmentPrompt(true);
    setCurrentAssessment(null);
    setParsedQuestions(null);
    setAssessmentAnswers({});
    setCurrentQuestionIndex(0);
    setCurrentTopicIndex(0);
    setGoalUpdateStatus({ status: '', message: '' });
  };

  // Function to get total number of questions across all topics
  const getTotalQuestions = () => {
    if (!parsedQuestions?.topics) return 0;
    return parsedQuestions.topics.reduce((total, topic) => total + topic.questions.length, 0);
  };

  // Function to get current absolute question index
  const getCurrentAbsoluteQuestionIndex = () => {
    if (!parsedQuestions?.topics) return 0;
    let absoluteIndex = 0;
    for (let i = 0; i < currentTopicIndex; i++) {
      absoluteIndex += parsedQuestions.topics[i].questions.length;
    }
    return absoluteIndex + currentQuestionIndex;
  };

  // Function to go to next question
  const handleNextQuestion = () => {
    if (!parsedQuestions?.topics) return;
    
    const currentTopic = parsedQuestions.topics[currentTopicIndex];
    
    // If there are more questions in current topic
    if (currentQuestionIndex < currentTopic.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } 
    // If there are more topics
    else if (currentTopicIndex < parsedQuestions.topics.length - 1) {
      setCurrentTopicIndex(currentTopicIndex + 1);
      setCurrentQuestionIndex(0);
    }
  };

  // Function to go to previous question
  const handlePreviousQuestion = () => {
    if (!parsedQuestions?.topics) return;
    
    // If there are previous questions in current topic
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    } 
    // If there are previous topics
    else if (currentTopicIndex > 0) {
      const previousTopic = parsedQuestions.topics[currentTopicIndex - 1];
      setCurrentTopicIndex(currentTopicIndex - 1);
      setCurrentQuestionIndex(previousTopic.questions.length - 1);
    }
  };

  // Function to check if current question is answered
  const isCurrentQuestionAnswered = () => {
    const answerKey = `${currentTopicIndex}-${currentQuestionIndex}`;
    return assessmentAnswers.hasOwnProperty(answerKey) && 
           assessmentAnswers[answerKey] !== '' && 
           assessmentAnswers[answerKey] !== null && 
           assessmentAnswers[answerKey] !== undefined;
  };

  // Function to check if this is the last question
  const isLastQuestion = () => {
    if (!parsedQuestions?.topics) return false;
    return currentTopicIndex === parsedQuestions.topics.length - 1 && 
           currentQuestionIndex === parsedQuestions.topics[currentTopicIndex].questions.length - 1;
  };

  // Function to handle topic selection change
  const handleTopicSelectionChange = (topicIndex, isSelected) => {
    setSelectedTopics(prev => ({
      ...prev,
      [topicIndex]: isSelected
    }));
  };

  // Function to proceed with roadmap generation after topic selection
  const handleProceedWithTopics = async () => {
    // Prevent proceeding if no topics are selected
    if (!Object.values(selectedTopics).some(v => v)) {
      setGoalUpdateStatus({
        status: 'error',
        message: 'Please select at least one topic to continue.'
      });
      return;
    }

    try {
      setGoalUpdateStatus({ 
        status: 'loading', 
        message: 'Generating your personalized learning plan...' 
      });

      // Collect selected topics
      const selectedTopicsList = [];
      if (topicRecommendations && topicRecommendations.length > 0) {
        topicRecommendations.forEach((topic, index) => {
          if (selectedTopics[index]) {
            selectedTopicsList.push(topic.title || 'Unknown Topic');
          }
        });
      }

      console.log('Selected topics for roadmap generation:', selectedTopicsList);

      // Goal already exists, just generate roadmap
      setLoaderMessage({ text: 'Generating Learning Roadmap...', subtext: "We're creating a personalized learning journey for you. This may take a few moments." });
      setRegenerating(true);
      document.getElementById('newGoalModal').style.display = 'none';
      document.body.style.overflow = 'hidden';

      try {
        const currentUser = await userService.getCurrentUser();

        console.log('Selected topics:', selectedTopicsList);
        // Pass selected topics to roadmap generation
        await roadmapService.createRoadmaps(currentUser.id, activeGoal.id, selectedTopicsList);
        setGoalUpdateStatus({ status: 'success', message: 'Roadmap generated successfully!' });
        setRegenerating(false);
        document.body.style.overflow = '';
        setTimeout(() => {
          setGoalUpdateStatus({ status: '', message: '' });
          navigate(`/learning-plan/${activeGoal.id}`);
        }, 2000);
      } catch (roadmapError) {
        console.error('Error generating roadmap:', roadmapError);
        setRegenerating(false);
        document.body.style.overflow = '';
        setGoalUpdateStatus({ status: 'success', message: 'Assessment completed! You can generate a roadmap later from the roadmap page.' });
        setTimeout(() => setGoalUpdateStatus({ status: '', message: '' }), 3000);
      }
      
      // Close modal and reset all states
      resetAssessmentStates();
      
    } catch (error) {
      console.error('Error proceeding with topics:', error);
      setGoalUpdateStatus({
        status: 'error',
        message: error.message || 'Failed to generate roadmap'
      });
    }
  };

  // Function to go back from topic selection
  const handleBackFromTopics = () => {
    setShowTopicSelection(false);
    setShowAssessmentQuestions(true);
    setTopicRecommendations([]);
    setSelectedTopics({});
    setGoalUpdateStatus({ status: '', message: '' });
  };

  // Function to handle close button click
  const handleCloseButtonClick = () => {
    // Check if user has made any progress that would be lost
    const hasProgress = newGoalDescription.trim() || 
                       showAssessmentPrompt || 
                       showAssessmentQuestions || 
                       showTopicSelection ||
                       Object.keys(assessmentAnswers).length > 0;
    
    if (hasProgress) {
      setShowCloseConfirmation(true);
    } else {
      resetAssessmentStates();
    }
  };

  // Function to confirm closing and lose progress
  const confirmCloseModal = () => {
    setShowCloseConfirmation(false);
    resetAssessmentStates();
  };

  // Function to cancel closing and keep modal open
  const cancelCloseModal = () => {
    setShowCloseConfirmation(false);
  };

  // Function to reset all assessment-related states
  const resetAssessmentStates = () => {
    document.getElementById('newGoalModal').style.display = 'none';
    setShowAssessmentPrompt(false);
    setShowAssessmentQuestions(false);
    setShowTopicSelection(false);
    setCurrentAssessment(null);
    setParsedQuestions(null);
    setAssessmentAnswers({});
          setSelectedPlannedCourseIndices({});
    setCurrentQuestionIndex(0);
    setCurrentTopicIndex(0);
    setChosenGoalDescription('');
    setTopicRecommendations([]);
    setSelectedTopics({});
    setGoalUpdateStatus({ status: '', message: '' });
    setShowCloseConfirmation(false);
    setPlanningResult(null);
    setClarifications([]);
    setCurrentClarifyingAnswer('');
  };

  return (
    <div className="app-layout">
      <SideMenu/>
      <div className={`learning-plan-main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${theme} ${isNewUser ? 'new-user-mode' : ''}`}>
        {goalUpdateStatus.status === 'error' && <div className="error-message">{goalUpdateStatus.message}</div>}
        
        {isLoading ? (
          <div className="sb-loading-container">
            <div className="loading-icon-container">
              <div className="empty-state-icon">🎯</div>
            </div>
            <div className="sb-loading-row">
              <div className="sb-loading-spinner"></div>
              <p className="sb-loading-text">Loading goals...</p>
            </div>
          </div>
        ) : (
          <div className={`goals-container ${theme} ${isNewUser ? 'new-user-container' : ''}`}>
            {isNewUser && (
              <div className="new-user-welcome">
                <div className="welcome-icon">🎉</div>
                <h2>Welcome to Brainy!</h2>
                <p>Let's get started by creating your first learning goal. Click the "Add New Goal" button below to begin your learning journey.</p>
              </div>
            )}
            <div className="goals-header">
              <div className="goals-panel-title">
                <Target size={24} />
                <h2 className="goals-header-title">Roadmap</h2>
              </div>
              <button 
                className={`goals-button ${isNewUser ? 'new-user-highlight' : ''}`}
                onClick={() => {
                  // Check goal limits before opening modal
                  if (!checkGoalLimits()) {
                    return; // Goal limit modal will be shown
                  }
                  
                  setNewGoalDescription('');
                  setGoalUpdateStatus({ status: '', message: '' });
                  setPlanningResult(null);
                  setClarifications([]);
                  setCurrentClarifyingAnswer('');
                  document.getElementById('newGoalModal').style.display = 'flex';
                  // Exit new user mode when they click the button
                  if (isNewUser) {
                    setIsNewUser(false);
                  }
                }}
              >
                <span>
                  <Target size={16} />
                  Add New Goal
                </span>
              </button>
            </div>

            <div className="goals-list">
              {goals.length === 0 && !isNewUser ? (
                <div className="no-goals">
                  <div className="empty-state-icon">🎯</div>
                  <p>You don't have any goals yet.</p>
                  <p>Let's create a goal to get started.</p>
                </div>
              ) : goals.length > 0 ? (
                <>
                  {/* Render active goal first */}
                  {activeGoal && !activeGoal.isCompleted && (
                    <div className="active-goal-section">
                      <h3 style={{textAlign: 'left'}}>
                        <Play size={20} className="active-goal-icon" />
                        Active Goal
                      </h3>
                      <div className={`goal-card active ${theme}`}>
                        <div className="goal-card-content">
                          
                          <div className="goal-thumbnail">
                            {activeGoal.imageUrl ? (
                              <img 
                                src={activeGoal.imageUrl.startsWith('http') 
                                  ? activeGoal.imageUrl 
                                  : `https://nd69zr.realhost-free.net${activeGoal.imageUrl}`
                                } 
                                alt={activeGoal.description}
                                className="goal-image"
                                onError={(e) => {
                                  console.error("Active goal image failed to load:", activeGoal.imageUrl);
                                  e.target.onerror = null;
                                  e.target.src = `https://nd69zr.realhost-free.net/goal-images/default-goal.svg`;
                                }}
                              />
                            ) : (
                              <div className="goal-icon">
                                <i className="goal-icon-image"></i>
                              </div>
                            )}
                          </div>
                          
                          <div className="goal-details">
                            <div className="goal-header">
                              <div className="goal-title-container">
                                <h4 className="goal-title">{activeGoal.description}</h4>
                              </div>
                              
                              <div className="goal-badges">
                                <span className="goal-active-badge">
                                  <Play size={12} />
                                  Active
                                </span>
                                {activeGoal.metadata && (
                                  <span 
                                    className="goal-level" 
                                    data-level={JSON.parse(activeGoal.metadata).level || "Not specified"}
                                  >
                                    {JSON.parse(activeGoal.metadata).level || "Not specified"}
                                  </span>
                                )}
                                <button 
                                  className="delete-goal-button top-right"
                                  onClick={() => openDeleteModal(activeGoal)}
                                  title="Delete goal"
                                >
                                  <Trash2 size={16} className="trash-icon" />
                                </button>
                              </div>
                            </div>
                            
                            <div className="goal-progress-container">
                              <div className="goal-progress-bar">
                                <div 
                                  className="goal-progress-fill" 
                                  style={{ width: `${getGoalProgress(activeGoal)}%` }}
                                ></div>
                              </div>
                              <div className="goal-progress-info">
                                <div className="goal-progress-text-container">
                                  <span className="goal-progress-percentage">{getGoalProgress(activeGoal)}% complete</span>
                                  {(() => {
                                    const { completedLessons, totalLessons } = getGoalProgressDetails(activeGoal);
                                    return totalLessons > 0 && (
                                      <span className="goal-progress-details">
                                        {completedLessons} of {totalLessons} lessons completed
                                      </span>
                                    );
                                  })()}
                                </div>
                                <div className="goal-metadata">
                                  <button 
                                    className="resume-goal-button"
                                    onClick={() => navigate(`/learning-plan/${activeGoal.id}`)}
                                  >
                                    <Play size={14} />
                                    Resume course
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Render other incomplete goals */}
                  {goals.filter(goal => !isGoalActive(goal) && !goal.isCompleted).length > 0 && (
                    <div className="other-goals-section">
                      <h3 style={{textAlign: 'left'}}>
                        <Target size={20} className="goals-section-icon" />
                        Goals
                      </h3>
                      {goals.filter(goal => !isGoalActive(goal) && !goal.isCompleted).map(goal => (
                        <div className={`goal-card ${theme}`} key={goal.id}>
                          <div className="goal-card-content">
                            <div className="goal-thumbnail">
                              {goal.imageUrl ? (
                                <img 
                                  src={goal.imageUrl.startsWith('http') 
                                    ? goal.imageUrl 
                                    : `https://nd69zr.realhost-free.net${goal.imageUrl}`
                                  }
                                  alt={goal.description} 
                                  className="goal-image"
                                  onError={(e) => {
                                    console.error("Goal image failed to load:", goal.imageUrl);
                                    e.target.onerror = null;
                                    e.target.src = `https://nd69zr.realhost-free.net/goal-images/default-goal.svg`;
                                  }}
                                />
                              ) : (
                                <div className="goal-icon">
                                  <i className="goal-icon-image"></i>
                                </div>
                              )}
                            </div>
                            
                            <div className="goal-details">
                              <div className="goal-header">
                                <div className="goal-title-container">
                                  <h4 className="goal-title">{goal.description}</h4>
                                </div>
                                
                                <div className="goal-badges">
                                  {goal.metadata && (
                                    <span 
                                      className="goal-level" 
                                      data-level={JSON.parse(goal.metadata).level || "Not specified"}
                                    >
                                      {JSON.parse(goal.metadata).level || "Not specified"}
                                    </span>
                                  )}
                                  <button 
                                    className="delete-goal-button top-right"
                                    onClick={() => openDeleteModal(goal)}
                                    title="Delete goal"
                                  >
                                    <Trash2 size={16} className="trash-icon" />
                                  </button>
                                </div>
                              </div>
                              
                              <div className="goal-progress-container">
                                <div className="goal-progress-bar">
                                  <div 
                                    className="goal-progress-fill" 
                                    style={{ width: `${getGoalProgress(goal)}%` }}
                                  ></div>
                                </div>
                                <div className="goal-progress-info">
                                  <div className="goal-progress-text-container">
                                    <span className="goal-progress-percentage">{getGoalProgress(goal)}% complete</span>
                                    {(() => {
                                      const { completedLessons, totalLessons } = getGoalProgressDetails(goal);
                                      return totalLessons > 0 && (
                                        <span className="goal-progress-details">
                                          {completedLessons} of {totalLessons} lessons completed
                                        </span>
                                      );
                                    })()}
                                  </div>
                                  <div className="goal-metadata">
                                    <button 
                                      className="set-active-button"
                                      onClick={() => handleSetActiveGoal(goal.id)}
                                    >
                                      <Target size={14} />
                                      Set as active
                                    </button>
                                  </div>
                                </div>
                              </div>
                              {(() => {
                                const courses = getGoalCourses(goal);
                                return courses.length > 0 && (
                                  <div className="goal-skills-section">
                                    <div className="goal-skills-title">Knowledge Units</div>
                                    <div className="planned-goal-skills">
                                      {courses.map((course, sIdx) => (
                                        <div key={sIdx} className="planned-skill-row">
                                          <div className="skill-info">
                                            <div className="skill-tags">
                                              <span className="tag-pill">{course.tag || course.Tag}</span>
                                            </div>
                                            {course.realisticDuration && (
                                              <span className="skill-duration">
                                                ⏱️ {course.realisticDuration} hours
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Render completed goals */}
                  {goals.filter(goal => goal.isCompleted).length > 0 && (
                    <div className="completed-goals-section">
                      <h3>
                        <CheckCircle size={20} className="completed-goals-icon" />
                        Completed Goals
                      </h3>
                      {goals.filter(goal => goal.isCompleted).map(goal => (
                        <div className={`goal-card completed ${theme}`} key={goal.id}>
                          <div className="goal-card-content">
                            <div className="goal-thumbnail">
                              {goal.imageUrl ? (
                                <img 
                                  src={goal.imageUrl.startsWith('http') 
                                    ? goal.imageUrl 
                                    : `https://nd69zr.realhost-free.net${goal.imageUrl}`
                                  }
                                  alt={goal.description}
                                  className="goal-image"
                                  onError={(e) => {
                                    console.error("Goal image failed to load:", goal.imageUrl);
                                    e.target.onerror = null;
                                    e.target.src = `https://nd69zr.realhost-free.net/goal-images/default-goal.svg`;
                                  }}
                                />
                              ) : (
                                <div className="goal-icon">
                                  <i className="goal-icon-image"></i>
                                </div>
                              )}
                              
                              {/* Completion Badge Overlay */}
                              <div className="goal-completion-badge">
                                <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <circle cx="12" cy="12" r="12" fill="#10B981"/>
                                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white" />
                                </svg>
                              </div>
                            </div>
                            
                            <div className="goal-details">
                              <div className="goal-header">
                                <div className="goal-title-container">
                                  <h4 className="goal-title">{goal.description}</h4>
                                </div>
                                
                                <div className="goal-badges">
                                  {goal.metadata && (
                                    <span 
                                      className="goal-level" 
                                      data-level={JSON.parse(goal.metadata).level || "Not specified"}
                                    >
                                      {JSON.parse(goal.metadata).level || "Not specified"}
                                    </span>
                                  )}
                                  <div className="goal-avg-score">
                                    Avg Score: 85%
                                  </div>
                                </div>
                              </div>
                              
                              <div className="goal-progress-container">
                                <div className="goal-progress-bar">
                                  <div 
                                    className="goal-progress-fill" 
                                    style={{ width: `${getGoalProgress(goal)}%` }}
                                  ></div>
                                </div>
                                <div className="goal-progress-info">
                                  <div className="goal-progress-text-container">
                                    <span className="goal-progress-percentage">{getGoalProgress(goal)}% complete</span>
                                    {(() => {
                                      const { completedLessons, totalLessons } = getGoalProgressDetails(goal);
                                      return totalLessons > 0 && (
                                        <span className="goal-progress-details">
                                          {completedLessons} of {totalLessons} lessons completed
                                        </span>
                                      );
                                    })()}
                                    <span className="goal-time-remaining">
                                      Completed on {goal.completedAt 
                                        ? new Date(goal.completedAt).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                          })
                                        : new Date().toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                          })
                                      }
                                    </span>
                                  </div>
                                  <div className="goal-metadata">
                                    <button 
                                      className="view-goal-button"
                                      onClick={() => navigate(`/learning-plan/${goal.id}`)}
                                    >
                                      <ChevronRight size={14} />
                                      View Roadmap
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        )}

        {/* Goal Creation Modal */}
        <div id="newGoalModal" className={`goals-modal ${theme}`}>
          <div className={`goals-modal-content ${theme}`}>
            <div className="goals-modal-header">
               <h3 className="goals-modal-title">Set Your Learning Goal</h3>
              <button
                className="goals-modal-close-button"
                onClick={handleCloseButtonClick}
              >
                ×
              </button>
            </div>
            
            <div className="goals-modal-body">
              {!showAssessmentPrompt && !showAssessmentQuestions && !showTopicSelection ? (
                <>
                  {!planningResult || !(planningResult.clarifyingQuestion || planningResult.ClarifyingQuestion) ? (
                    <div className="goals-modal-form-group">
                      <label htmlFor="newGoal">Goal Description</label>
                      <textarea
                        id="newGoal"
                        value={newGoalDescription}
                        onChange={(e) => setNewGoalDescription(e.target.value)}
                        placeholder="e.g., 'Master React functional components and hooks', 'Learn Python for data analysis'"
                        rows={3}
                      />
                      <small>Be specific about what you want to achieve.</small>
                    </div>
                  ) : (
                    <div className="goals-modal-form-group">
                      <label htmlFor="clarifyAnswer">{planningResult.clarifyingQuestion || planningResult.ClarifyingQuestion}</label>
                      <input
                        id="clarifyAnswer"
                        type="text"
                        value={currentClarifyingAnswer}
                        onChange={(e) => setCurrentClarifyingAnswer(e.target.value)}
                        placeholder="Type your answer"
                      />
                      {clarifications.length > 0 && (
                        <small>Previous clarifications provided: {clarifications.length}</small>
                      )}
                    </div>
                  )}

                  {/* Desired difficulty removed */}
                </>
              ) : showAssessmentQuestions ? (
                <div className="goals-modal-assessment-questions">
                  <div className="assessment-header">
                    <h4 style={{ textAlign: 'left' }}>Knowledge Assessment</h4>
                    <div className="assessment-progress">
                      <span className="assessment-progress-text">
                        Question {getCurrentAbsoluteQuestionIndex() + 1} of {getTotalQuestions()}
                      </span>
                      <span className="assessment-topic-info">
                        Topic: {parsedQuestions?.topics?.[currentTopicIndex]?.title || 'Loading...'}
                      </span>
                      <div className="assessment-progress-bar">
                        <div 
                          className="assessment-progress-fill" 
                          style={{ width: `${((getCurrentAbsoluteQuestionIndex() + 1) / getTotalQuestions()) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  
                  {parsedQuestions?.topics && parsedQuestions.topics.length > 0 && (
                    <div className="assessment-single-question">
                      {(() => {
                        const currentTopic = parsedQuestions.topics[currentTopicIndex];
                        const currentQuestion = currentTopic?.questions?.[currentQuestionIndex];
                        
                        if (!currentQuestion) {
                          return (
                            <div className="assessment-question current">
                              <h5>Loading Question...</h5>
                              <p className="question-text">Please wait while we load the question.</p>
                            </div>
                          );
                        }

                        const answerKey = `${currentTopicIndex}-${currentQuestionIndex}`;

                        return (
                          <div className="assessment-question current">
                            <h5>Question {getCurrentAbsoluteQuestionIndex() + 1}</h5>
                            <div className="question-topic-badge">
                              {currentTopic.title}
                            </div>
                            <p className="question-text">{currentQuestion.question}</p>
                            <div className="question-options">
                              {currentQuestion.type === 'multiple-choice' ? (
                                // Multiple choice question
                                <div className="multiple-choice-options">
                                  {currentQuestion.options?.map((option, optionIndex) => {
                                    const optionLabel = String.fromCharCode(65 + optionIndex); // A, B, C, D
                                    return (
                                      <label key={optionIndex} className="option-label">
                                        <input
                                          type="radio"
                                          name={`question-${answerKey}`}
                                          value={option}
                                          checked={assessmentAnswers[answerKey] === option}
                                          onChange={(e) => handleAssessmentAnswer(currentTopicIndex, currentQuestionIndex, e.target.value)}
                                        />
                                        <span className="option-text">
                                          <strong>{optionLabel}:</strong> {option}
                                        </span>
                                      </label>
                                    );
                                  })}
                                </div>
                              ) : (
                                // Open-ended question
                                <div className="open-ended-question">
                                  <textarea
                                    value={assessmentAnswers[answerKey] || ''}
                                    onChange={(e) => handleAssessmentAnswer(currentTopicIndex, currentQuestionIndex, e.target.value)}
                                    placeholder={`Enter your answer here... ${currentQuestion.expectedLength ? `(Expected length: ${currentQuestion.expectedLength})` : ''}`}
                                    rows={4}
                                    className="open-ended-textarea"
                                  />
                                  {currentQuestion.expectedLength && (
                                    <small className="expected-length-hint">
                                      Expected length: {currentQuestion.expectedLength}
                                    </small>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  
                  <div className="goals-modal-assessment-actions">
                    <div className="assessment-navigation">
                      {currentQuestionIndex === 0 ? (
                        <button
                          className="goals-modal-cancel-button"
                          onClick={handleBackFromAssessment}
                          disabled={submittingAssessment}
                        >
                          Back
                        </button>
                      ) : (
                        <button
                          className="goals-modal-back-button"
                          onClick={handlePreviousQuestion}
                          disabled={submittingAssessment}
                        >
                          <ChevronLeft size={16} />
                          Previous
                        </button>
                      )}
                      
                      {isLastQuestion() ? (
                        <button
                          className="goals-modal-confirm-button"
                          onClick={handleSubmitAssessment}
                          disabled={submittingAssessment || !isCurrentQuestionAnswered()}
                        >
                          {submittingAssessment ? (
                            <span>
                              <div className="sb-loading-spinner"></div>
                              Submitting...
                            </span>
                          ) : (
                            'Complete Assessment'
                          )}
                        </button>
                      ) : (
                        <button
                          className="goals-modal-confirm-button"
                          onClick={handleNextQuestion}
                          disabled={!isCurrentQuestionAnswered()}
                        >
                          Next
                          <ChevronRight size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ) : showTopicSelection ? (
                <div className="goals-modal-topic-selection">
                  <h4 style={{ textAlign: 'left' }}>Customize your learning path</h4>
                  <p>
                    Based on your assessment results, we've identified topics to include in your learning plan. 
                    You can adjust which topics to focus on:
                  </p>
                  
                  <div className="topic-selection-list">
                    {topicRecommendations.map((topic, index) => (
                      <div key={index} className="topic-selection-item">
                        <label className="topic-checkbox-label">
                          <input
                            type="checkbox"
                            checked={selectedTopics[index] || false}
                            onChange={(e) => handleTopicSelectionChange(index, e.target.checked)}
                            className="topic-checkbox"
                          />
                          <div className="topic-info">
                            <span className="topic-title">{topic.title}</span>
                            <div className="topic-recommendation">
                              {topic.recommendToComplete ? (
                                <span className="recommendation-badge recommended">
                                  📚 Recommended - Focus needed
                                </span>
                              ) : (
                                <span className="recommendation-badge mastered">
                                  ✅ Already mastered - Skip recommended
                                </span>
                              )}
                            </div>
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                  
                  {goalUpdateStatus.status === 'error' && (
                    <div className="goals-modal-save-status error" style={{marginBottom: '12px'}}>
                      {goalUpdateStatus.message}
                    </div>
                  )}
                  
                  <div className="goals-modal-topic-actions">
                    <button
                      className="goals-modal-cancel-button"
                      onClick={handleBackFromTopics}
                      disabled={regenerating}
                    >
                      Back to Assessment
                    </button>
                    <button
                      className="goals-modal-confirm-button"
                      onClick={handleProceedWithTopics}
                      disabled={regenerating || !Object.values(selectedTopics).some(v => v)}
                    >
                      {regenerating ? (
                        <span>
                          <div className="sb-loading-spinner"></div>
                          Creating Plan...
                        </span>
                      ) : (
                        'Create Learning Plan'
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="goals-modal-assessment-prompt">
                  {planningResult && (planningResult.clarifyingQuestion || planningResult.ClarifyingQuestion) ? (
                    <p>{planningResult.clarifyingQuestion || planningResult.ClarifyingQuestion}</p>
                  ) : getPlannedGoal(planningResult) ? (
                    <div style={{ textAlign: 'left' }}>
                      <div className={`typed-goal-block ${theme}`}>
                        <strong>Goal:</strong> {getPlannedGoal(planningResult).title || getPlannedGoal(planningResult).Title || newGoalDescription}
                      </div>
                      <h5>Recommended courses <br/> Select ones that you want to go on in this goal</h5>
                      <button
                        className="select-all-button"
                        onClick={() => {
                          const allIndices = getGoalCourses(getPlannedGoal(planningResult)).map((_, idx) => idx);
                          const allSelected = allIndices.every(idx => selectedPlannedCourseIndices[idx]);
                          const newSelections = {};
                          if (!allSelected) {
                            // Select all
                            allIndices.forEach(idx => {
                              newSelections[idx] = true;
                            });
                          }
                          // If all are selected, clicking will deselect all (empty object)
                          setSelectedPlannedCourseIndices(newSelections);
                        }}
                        type="button"
                      >
                        {(() => {
                          const allIndices = getGoalCourses(getPlannedGoal(planningResult)).map((_, idx) => idx);
                          const allSelected = allIndices.every(idx => selectedPlannedCourseIndices[idx]);
                          const someSelected = allIndices.some(idx => selectedPlannedCourseIndices[idx]);
                          return allSelected ? 'Deselect All' : someSelected ? 'Select All' : 'Select All';
                        })()}
                      </button>
                      <ul className={`recommendations-list ${theme}`}>
                        {getGoalCourses(getPlannedGoal(planningResult)).map((course, cIdx) => (
                          <li key={cIdx} className="recommendation-item">
                            <input
                              type="checkbox"
                              className="planned-goal-checkbox"
                              checked={!!selectedPlannedCourseIndices[cIdx]}
                              onChange={() => handleTogglePlannedCourse(cIdx, course.title || course.Title)}
                              aria-label={`Select course ${course.title || course.Title}`}
                            />
                            <span className="recommendation-text">{course.title || course.Title}</span>
                            <div className="planned-course-info">
                              <div className="course-info-row">
                                <div className="course-details">
                                  {course.description && (
                                    <div className="course-description">
                                      {course.description}
                                    </div>
                                  )}
                                  <div className="course-meta">
                                    {course.complexityLevel && (
                                      <span className="course-complexity">
                                        Level: {course.complexityLevel}
                                      </span>
                                    )}
                                    {course.desiredDuration && (
                                      <span className="course-duration">
                                        ⏱️ {course.desiredDuration} hours
                                      </span>
                                    )}
                                  </div>
                                  {Array.isArray(course.tags) && course.tags.length > 0 && (
                                    <div className="course-tags">
                                      {course.tags.map((tag, tagIdx) => (
                                        <span key={tagIdx} className="tag-pill">{tag}</span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <p>
                      Something gone wrong. Try to go back and start over.
                    </p>
                  )}
                  {goalUpdateStatus.status === 'error' && (
                    <div className="goals-modal-save-status error" style={{marginBottom: '12px'}}>
                      {goalUpdateStatus.message}
                    </div>
                  )}
                  {/* Assessment actions removed per request */}
                </div>
              )}
            </div>

            <div className="goals-modal-footer">
              {!showAssessmentPrompt && !showAssessmentQuestions && !showTopicSelection ? (
                (!planningResult || !(planningResult.clarifyingQuestion || planningResult.ClarifyingQuestion)) ? (
                  <button
                    className="goals-modal-confirm-button"
                    onClick={handleNextClick}
                    disabled={planningLoading}
                  >
                    {planningLoading ? (
                      <span>
                        <div className="sb-loading-spinner"></div>
                        Loading...
                      </span>
                    ) : (
                      <span>
                        <ChevronRight size={16} />
                        Next
                      </span>
                    )}
                  </button>
                ) : (
                  <>
                    <button
                      className="goals-modal-back-button"
                      onClick={handleClarificationBack}
                      disabled={planningLoading}
                      style={{ marginRight: 'auto', paddingTop: 12, paddingBottom: 12 }}
                    >
                      <ChevronLeft size={16} />
                      Back
                    </button>
                    <button
                      className="goals-modal-confirm-button"
                      onClick={handleClarificationNext}
                      disabled={planningLoading}
                      style={{ paddingTop: 12, paddingBottom: 12 }}
                    >
                      {planningLoading ? (
                        <span>
                          <div className="sb-loading-spinner"></div>
                          Loading...
                        </span>
                      ) : (
                        <span>
                          <ChevronRight size={16} />
                          Next
                        </span>
                      )}
                    </button>
                  </>
                )
              ) : showAssessmentQuestions || showTopicSelection ? (
                // No footer buttons for assessment questions or topic selection - they're in the body
                null
              ) : (
                <div className="goals-modal-footer-row">
                  <button
                    className="goals-modal-back-button"
                    onClick={() => {
                      setShowAssessmentPrompt(false);
                      // Reset to initial form
                      setChosenGoalDescription('');
                    }}
                    style={{ marginRight: 'auto' }}
                  >
                    <ChevronLeft size={16} />
                    Back
                  </button>
                  <button
                    className="goals-modal-confirm-button"
                    onClick={handleSubmitPlannedCourses}
                    disabled={regenerating || Object.keys(selectedPlannedCourseIndices).length === 0}
                  >
                    {regenerating ? (
                      <span>
                        <div className="sb-loading-spinner"></div>
                        Submitting...
                      </span>
                    ) : (
                      'Submit'
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Display status messages inside modal */}
            {goalUpdateStatus.message && goalUpdateStatus.status !== 'success' && (
              <div className={`goals-modal-save-status ${goalUpdateStatus.status}`} style={{marginTop: '15px'}}>
                {goalUpdateStatus.message}
              </div>
            )}
          </div>
        </div>

        {/* Display success messages outside modal */}
        {goalUpdateStatus.message && goalUpdateStatus.status === 'success' && (
          <div className={`goals-modal-save-status ${goalUpdateStatus.status}`} style={{marginTop: '15px', textAlign: 'center'}}>
            {goalUpdateStatus.message}
          </div>
        )}
        
        {/* Delete Confirmation Modal */}
        {isDeleteModalOpen && (
          <div className={`goals-modal ${theme}`} style={{display: 'flex'}}>
            <div className={`goals-modal-content goals-modal-delete-confirmation ${theme}`}>
              <div className="goals-modal-header">
                <h3 className="goals-modal-title">Confirm Deletion</h3>
                <button className="goals-modal-close-button" onClick={closeDeleteModal}>×</button>
              </div>
              <div className="goals-modal-body">
                <div className="goals-modal-delete-warning">
                  <p><strong>Warning:</strong> Deleting this goal will permanently remove:</p>
                  <ul className="goals-modal-list">
                    <li>The goal "{goalToDelete?.description}"</li>
                    <li>All roadmap content</li>
                    <li>Learning plans associated with this goal</li>
                    <li>All lessons and materials</li>
                    <li>Your progress and assessment results</li>
                  </ul>
                  <p className="goals-modal-paragraph">This action cannot be undone.</p>
                </div>
              </div>
              <div className="goals-modal-footer">
                <button 
                  className="goals-modal-cancel-button" 
                  onClick={closeDeleteModal}
                >
                  Cancel
                </button>
                <button 
                  className="goals-modal-confirm-button delete" 
                  onClick={confirmDeleteGoal}
                >
                  <Trash2 size={16} />
                  Delete Permanently
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Close Confirmation Modal */}
        {showCloseConfirmation && (
          <div className={`goals-modal ${theme}`} style={{display: 'flex'}}>
            <div className={`goals-modal-content goals-modal-close-confirmation ${theme}`}>
              <div className="goals-modal-header">
                <h3 className="goals-modal-title">Confirm Close</h3>
              </div>
              <div className="goals-modal-body">
                <div className="goals-modal-close-warning">
                  <p><strong>Warning:</strong> You have unsaved progress that will be lost if you close this window.</p>
                  <p>This includes:</p>
                  <ul className="goals-modal-list">
                    {newGoalDescription.trim() && <li>Your goal description: "{newGoalDescription.trim()}"</li>}
                    {showAssessmentPrompt && <li>Assessment setup</li>}
                    {(showAssessmentQuestions || Object.keys(assessmentAnswers).length > 0) && <li>Assessment answers</li>}
                    {showTopicSelection && <li>Topic selections</li>}
                  </ul>
                  <p className="goals-modal-paragraph">Are you sure you want to close and lose this progress?</p>
                </div>
              </div>
              <div className="goals-modal-footer">
                <button 
                  className="goals-modal-cancel-button" 
                  onClick={cancelCloseModal}
                >
                  Keep Working
                </button>
                <button 
                  className="goals-modal-confirm-button delete" 
                  onClick={confirmCloseModal}
                >
                  Close & Lose Progress
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Goal Limit Modal */}
        {showGoalLimitModal && (
          <div className={`goals-modal ${theme}`} style={{display: 'flex'}}>
            <div className={`goals-modal-content goals-modal-goal-limit ${theme}`}>
              <div className="goals-modal-header">
                <h3 className="goals-modal-title">
                  {goalLimitType === 'free' ? 'Goal Limit Reached' : 'Maximum Goals Reached'}
                </h3>
                <button className="goals-modal-close-button" onClick={closeGoalLimitModal}>×</button>
              </div>
              <div className="goals-modal-body">
                <div className="goals-modal-limit-warning">
                  {goalLimitType === 'free' ? (
                    <>
                      <div className="limit-icon">🎯</div>
                      <p className="limit-message">
                        <strong>Free Plan Limit:</strong> You can only have <strong>1 goal in progress</strong> with the basic free subscription.
                      </p>
                      <p className="limit-suggestion">
                        Complete your current goal or upgrade to Pro to create unlimited goals and unlock premium features!
                      </p>
                      <div className="limit-features">
                        <h4>Pro Plan Benefits:</h4>
                        <ul>
                          <li>✨ Up to 10 goals in progress</li>
                          <li>🚀 Advanced learning features</li>
                          <li>📊 Detailed progress analytics</li>
                          <li>🎨 Premium themes and customization</li>
                          <li>💬 Priority support</li>
                        </ul>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="limit-icon">🏆</div>
                      <p className="limit-message">
                        <strong>Pro Plan Limit:</strong> You've reached the maximum of <strong>10 goals in progress</strong>.
                      </p>
                      <p className="limit-suggestion">
                        Complete some of your existing goals before creating new ones. This helps you stay focused and achieve better results!
                      </p>
                    </>
                  )}
                </div>
              </div>
              <div className="goals-modal-footer">
                {goalLimitType === 'free' ? (
                  <>
                    <button 
                      className="goals-modal-cancel-button" 
                      onClick={closeGoalLimitModal}
                    >
                      Continue with Free
                    </button>
                    <button 
                      className="goals-modal-confirm-button upgrade" 
                      onClick={handleGetPro}
                    >
                      Get Pro
                    </button>
                  </>
                ) : (
                  <button 
                    className="goals-modal-confirm-button" 
                    onClick={closeGoalLimitModal}
                  >
                    I Understand
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Loading Overlay - Match Roadmap approach */}
        {regenerating && (
          <div className={`sb-loader-overlay ${theme === 'dark' ? 'dark' : ''}`}>
            <div className="loading-icon-container">
              <div className="empty-state-icon">🎯</div>
            </div>
            <div className="sb-loader-content">
              <div className="sb-loader-spinner"></div>
              <p className="sb-loader-text">{loaderMessage.text || "Working on your request..."}</p>
            </div>
            <p className="sb-loader-subtext">{loaderMessage.subtext || "This may take a few moments."}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoalsPage; 